import { useState } from "react";
import { FileSpreadsheet, FileText } from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { useAuth } from "#/context/AuthProvider";
import { exportToExcel, exportToPdf } from "#/lib/exportUtils";
import { useAllTrainingRequests } from "../hooks";
import type { TrainingRequest, TrainingRequestStatus } from "../types";

const STATUSES: TrainingRequestStatus[] = [
  "DRAFT", "SUBMITTED", "APPROVED", "REJECTED", "CANCELLED", "COMPLETED",
];

const STATUS_STYLES: Record<TrainingRequestStatus, string> = {
  DRAFT:     "bg-slate-100 text-slate-700",
  SUBMITTED: "bg-amber-100 text-amber-800",
  APPROVED:  "bg-emerald-100 text-emerald-800",
  REJECTED:  "bg-red-100 text-red-700",
  CANCELLED: "bg-slate-200 text-slate-600",
  COMPLETED: "bg-indigo-100 text-indigo-800",
};

function fmt(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return v; }
}

function fmtDateTime(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return v; }
}

/** Employee + Training Course export, used for both Excel and PDF. The Cost column is left
 *  out entirely for viewers without financial rights — not just blanked, so it never ends up
 *  in a downloaded file either. */
function buildExportRows(rows: TrainingRequest[], includeCost: boolean) {
  const headers = includeCost
    ? ["Employee", "Department", "Training Course", "Category", "Status", "Submitted", "Approved", "Cost (KES)"]
    : ["Employee", "Department", "Training Course", "Category", "Status", "Submitted", "Approved"];
  const data = rows.map((r) => {
    const base = [
      r.employeeName ?? "—",
      r.departmentName ?? "—",
      r.trainingTitle,
      r.categoryName ?? "—",
      r.status.replace(/_/g, " "),
      fmt(r.submittedAt),
      fmt(r.approvedAt),
    ];
    return includeCost ? [...base, r.applicantEstimatedCost ?? 0] : base;
  });
  return { headers, data };
}

export default function TrainingReportsPage() {
  const { hasRole, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasRole("ADMIN");
  // Everyone who can reach this page (gated to HR_TRAINING_VIEW at the route level) can see
  // who requested what and its status. Cost figures are a further restriction, on top of
  // that, to those who actually hold financial-approval rights.
  const canViewFinancials = isAdmin || hasPermission("HR_FINANCE_APPROVE") || hasPermission("HR_TRAINING_MANAGE");

  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Fetch a large page to build client-side summaries
  const query = useAllTrainingRequests(0, 200);

  if (query.isLoading) return <PageLoader label="Loading training report…" />;
  if (query.isError) return <ErrorState title="Failed to load report" message={String(query.error)} />;

  const all = query.data?.content ?? [];

  // ── summary stats ──
  const statusCounts = STATUSES.reduce<Record<string, number>>((acc, s) => {
    acc[s] = all.filter((r) => r.status === s).length;
    return acc;
  }, {});

  const totalCost = all
    .filter((r) => r.applicantEstimatedCost != null)
    .reduce((sum, r) => sum + (r.applicantEstimatedCost ?? 0), 0);

  const approvedCost = all
    .filter((r) => r.status === "APPROVED" && r.applicantEstimatedCost != null)
    .reduce((sum, r) => sum + (r.applicantEstimatedCost ?? 0), 0);

  // ── by category ──
  const byCat: Record<string, { name: string; count: number; cost: number }> = {};
  all.forEach((r) => {
    const key = String(r.categoryId ?? "uncategorised");
    if (!byCat[key]) byCat[key] = { name: r.categoryName ?? "Uncategorised", count: 0, cost: 0 };
    byCat[key].count++;
    byCat[key].cost += r.applicantEstimatedCost ?? 0;
  });
  const catRows = Object.values(byCat).sort((a, b) => b.count - a.count);

  // ── recent approvals ──
  const recentApproved = all
    .filter((r) => r.status === "APPROVED")
    .sort((a, b) => (b.approvedAt ?? "").localeCompare(a.approvedAt ?? ""))
    .slice(0, 10);

  // ── full list, newest first ──
  const allSorted = [...all].sort((a, b) => (b.submittedAt ?? b.createdAt ?? "").localeCompare(a.submittedAt ?? a.createdAt ?? ""));

  async function handleExportExcel() {
    setExportingExcel(true);
    try {
      const { headers, data } = buildExportRows(allSorted, canViewFinancials);
      await exportToExcel("Training Requests Report", headers, data, `training-requests-${new Date().toISOString().slice(0, 10)}`);
    } finally {
      setExportingExcel(false);
    }
  }

  async function handleExportPdf() {
    setExportingPdf(true);
    try {
      const { headers, data } = buildExportRows(allSorted, canViewFinancials);
      await exportToPdf({
        title: "Training Requests Report",
        headers,
        rows: data,
        filename: `training-requests-${new Date().toISOString().slice(0, 10)}`,
      });
    } finally {
      setExportingPdf(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Training Reports</h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={exportingExcel || allSorted.length === 0}
            className="flex items-center gap-1.5 rounded-xl border border-emerald-300 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
            {exportingExcel ? "Exporting…" : "Export Excel"}
          </button>
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={exportingPdf || allSorted.length === 0}
            className="flex items-center gap-1.5 rounded-xl border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            <FileText className="h-3.5 w-3.5" />
            {exportingPdf ? "Exporting…" : "Export PDF"}
          </button>
        </div>
      </div>

      {/* Status summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {STATUSES.map((s) => (
          <div key={s} className={`rounded-2xl p-4 ${STATUS_STYLES[s]}`}>
            <p className="text-2xl font-black">{statusCounts[s] ?? 0}</p>
            <p className="text-xs font-semibold mt-0.5">{s.replace(/_/g, " ")}</p>
          </div>
        ))}
      </div>

      {/* Cost summary — financial rights only */}
      {canViewFinancials && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Total Requested Cost</p>
            <p className="text-3xl font-black text-slate-800 mt-1">
              KES {totalCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-slate-400 mt-1">Across all {all.length} requests</p>
          </div>
          <div className="rounded-2xl border bg-emerald-50 border-emerald-200 p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Approved Cost</p>
            <p className="text-3xl font-black text-emerald-800 mt-1">
              KES {approvedCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-emerald-600 mt-1">{statusCounts["APPROVED"] ?? 0} approved requests</p>
          </div>
        </div>
      )}

      {/* By category */}
      {catRows.length > 0 && (
        <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b bg-slate-50">
            <h2 className="font-bold text-slate-800 text-sm">Requests by Category</h2>
          </div>
          <div className="divide-y">
            {catRows.map((c) => (
              <div key={c.name} className="flex items-center justify-between px-5 py-3 text-sm">
                <span className="font-semibold text-slate-700">{c.name}</span>
                <div className="flex gap-6 text-right text-slate-500">
                  <span><strong className="text-slate-800">{c.count}</strong> request{c.count !== 1 ? "s" : ""}</span>
                  {canViewFinancials && c.cost > 0 && <span>KES <strong className="text-slate-800">{c.cost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong></span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent approvals */}
      {recentApproved.length > 0 && (
        <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b bg-slate-50">
            <h2 className="font-bold text-slate-800 text-sm">Recently Approved</h2>
          </div>
          <div className="divide-y">
            {recentApproved.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm">
                <div>
                  <p className="font-semibold text-slate-800">{r.trainingTitle}</p>
                  <p className="text-xs text-slate-400">{r.employeeName} · Approved {fmtDateTime(r.approvedAt)}</p>
                </div>
                {canViewFinancials && r.applicantEstimatedCost != null && (
                  <span className="text-xs font-semibold text-emerald-700">
                    KES {r.applicantEstimatedCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All training requests — employee + course, the exportable detail list */}
      {allSorted.length > 0 && (
        <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b bg-slate-50">
            <h2 className="font-bold text-slate-800 text-sm">All Training Requests</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3">Training Course</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3">Approved</th>
                  {canViewFinancials && <th className="px-4 py-3 text-right">Cost</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {allSorted.map((r, idx) => (
                  <tr key={r.id} className={idx % 2 === 1 ? "bg-slate-50/60" : "bg-white"}>
                    <td className="px-4 py-3 font-medium text-slate-900">{r.employeeName ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-700">{r.trainingTitle}</td>
                    <td className="px-4 py-3 text-slate-500">{r.categoryName ?? "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[r.status]}`}>{r.status.replace(/_/g, " ")}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{fmt(r.submittedAt)}</td>
                    <td className="px-4 py-3 text-slate-500">{fmt(r.approvedAt)}</td>
                    {canViewFinancials && (
                      <td className="px-4 py-3 text-right text-slate-700">
                        {r.applicantEstimatedCost != null ? `KES ${r.applicantEstimatedCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
